import { formatPercentage } from "@sb1/ffe-formatters";
import MoneyBag from "@sb1/ffe-icons/icons/open/300/xl/money_bag.svg";
import HakeStjerneIkon from "@sb1/ffe-icons/icons/open/300/xl/new_releases.svg";
import Savings from "@sb1/ffe-icons/icons/open/300/xl/savings.svg";
import { Icon } from "@sb1/ffe-icons-react";
import type { ReactNode } from "react";
import krSirkelPilIkon from "$IMAGES/kr_sirkel_pil_ikon.svg";
import type { F2100PolicyDetailsDto } from "~/shared/api/usePolicyDetails";
import type { ProductType } from "~/shared/api/usePoliseOversikt";
import { recommendedPercentages } from "~/shared/components/kartlegging/utils";
import type { Policy, PolicyView } from "~/shared/reducers/policies/types";
import { products, type Products } from "~/utils/new-savings";
import { nbsp } from "~/utils/unicode";
import { AKTIV_UNDER_UTBETALING, UNDER_UTBETALING, UTBETALING_AVSLUTTET, UTBETALING_BESTILT } from "~/utils/utbetaling";
import type { GablerPoliseDetaljerDto } from "~/avtale-detaljer/gabler/avtaledetaljer/hooks/useHentGablerPolise";

export const itpMedInvesteringsvalg = "11";
export const innskuddtjenestepensjonMedGarantiProductId = "31";
export const klvrLivrenteProductId = "35";
export const klvrLivrenteGarantiProductId = "39";
export const oldIpsProductId = "44";
export const loProductId = "45";
export const pkbItpProductId = "46";
export const pkbMedGarantiProductId = "47";
export const pkbLivrenteProductId = "48";
export const pkbLivrenteGarantiProductId = "49";
export const oldPensjonskontoProductId = "51";
export const ipaProductId = "52";
export const ipaEtterlatteProductId = "53";
export const fundAccount = "54";
export const garantiProductId = "55";
export const livrenteProductId = "56";
export const livrenteEtterlatteProductId = "57";
export const oldIpsWithGarantiProductId = "58";
export const hybridMedInvesteringsvalg = "59";
export const pbgProductId = "63";
export const pensjonskontoProductId = "65";
export const hybridMedGarantiProductId = "66";
export const ipsProductId = "73";
export const ipsWithGarantiProductId = "74";
export const epkProductId = "75";
export const loEpkProductId = "76";
export const loPbkProductId = "78";
export const pkbEtterEpkProductId = "79";
export const pensjonskontoLoProductId = "95";
export const ipsLoProductId = "96";
export const ipsWithGarantiLoProductId = "97";
export const itpMedGarantiEtterlatteProductId = "104";
export const IPS_PSK = [pensjonskontoProductId, ipsProductId];
export const PENSION_PRODUCT_IDS = [pensjonskontoProductId, pensjonskontoLoProductId, oldPensjonskontoProductId];
export const IPS_PRODUCT_IDS = [ipsProductId, ipsLoProductId, ipsWithGarantiProductId, ipsWithGarantiLoProductId];
export const IPS_INVESTERINGSVALG_PRODUCT_IDS = [ipsProductId, ipsLoProductId];
export const IPS_GARANTI_PRODUCT_IDS = [ipsWithGarantiProductId, ipsWithGarantiLoProductId];
export const OLD_IPS_PRODUCT_IDS = [oldIpsProductId, oldIpsWithGarantiProductId];
export const PENSJONSKONTO_PRODUCT_IDS = [pensjonskontoProductId, pensjonskontoLoProductId];
export const HYBRID_PRODUCT_IDS = [hybridMedInvesteringsvalg, pbgProductId, "66"];
export const PRODUKTER_MED_ABEL_HELSEHJELP = [itpMedInvesteringsvalg, hybridMedInvesteringsvalg, innskuddtjenestepensjonMedGarantiProductId, hybridMedGarantiProductId];

// Kunden kan tilbys helsehjelp fra ABEL på avtaledetaljsiden når polisen har uføredekning (CP0),
// kunden ikke selv er ufør, polisen ikke har Mentor-helsehjelp, og produktet støtter Abel-helsehjelp.
// Norlandia-poliser er også omfattet så lenge kriteriene over er oppfylt.
export function harAbelHelsehjelp(polise: F2100PolicyDetailsDto | PolicyView): boolean {
	const f2100Polise = polise as F2100PolicyDetailsDto;
	const poliseView = polise as PolicyView;
	const harDekning = f2100Polise?.coverages?.some((coverage) => coverage.coverageId === "CP0") ?? poliseView.uforedekning;
	const harUforedekning = harDekning && !polise.konfig.ufoer;
	return harUforedekning && !polise.hasMentorHelsehjelp && PRODUKTER_MED_ABEL_HELSEHJELP.includes(polise.produktId);
}

export const ETTERLATTE_PRODUCT_IDS = ["103", "104", "105", "106", "107", "108", "109", "110", "111", "112", "113", "114", "115", "116", "117"];
export const DATE_CHANGE_ENABLED_PRODUCTS = [
	fundAccount,
	garantiProductId,
	ipaProductId,
	ipaEtterlatteProductId,
	ipsProductId,
	ipsLoProductId,
	livrenteProductId,
	livrenteEtterlatteProductId,
	loProductId,
	oldIpsProductId,
	oldPensjonskontoProductId,
	pensjonskontoProductId,
	pensjonskontoLoProductId,
];

export const HIGHEST_CONVERTED_POLICY_NUMBER = 215711;

export const PRISFORDEL_PRODUCT_IDS = [pensjonskontoProductId, oldPensjonskontoProductId, ipsProductId, ipsWithGarantiProductId];

export const FRIPOLISE_PRODUCT_IDS = ["1", "2", "3"];

export const DEATH_COVERAGE_PRODUCT_IDS = [fundAccount, garantiProductId, livrenteProductId, pensjonskontoProductId, pensjonskontoLoProductId, "7", "8", "9"];

export const DISABLED_COVERAGE_IDS = [loProductId, oldPensjonskontoProductId];

export const SAVING_AMOUNT_EDITABLE_PRODUCT_IDS = [fundAccount, ipsProductId, ipsLoProductId, loProductId, oldPensjonskontoProductId, pensjonskontoProductId, pensjonskontoLoProductId];

export const PRODUCT_IDS_WITH_LOWER_COST_ADVANTAGE = [pensjonskontoProductId, ipsProductId, ipsWithGarantiProductId];
export const produktMedPrisfordel = (id: string) => PRODUCT_IDS_WITH_LOWER_COST_ADVANTAGE.includes(id);

export const PRODUCT_IDS_WITH_FLEXIBLE_DISBURSEMENT = [
	fundAccount,
	garantiProductId,
	livrenteProductId,
	livrenteEtterlatteProductId,
	oldPensjonskontoProductId,
	pensjonskontoProductId,
	pensjonskontoLoProductId,
];

export const NJORD_UTBETALING_PRODUCTS = ["1", "2", "3", "6", "7", "8", "9"];

const PRODUKTER_MED_SKJULT_SPAREPROFILDETAILS = [
	innskuddtjenestepensjonMedGarantiProductId,
	pkbMedGarantiProductId,
	hybridMedGarantiProductId,
	pbgProductId,
	garantiProductId,
	itpMedGarantiEtterlatteProductId,
];

export const skjulSpareprofilDetails = (productId: string, underUtbetaling?: boolean) => {
	const garantiProdukt = PRODUKTER_MED_SKJULT_SPAREPROFILDETAILS.includes(productId);
	const ipsGarantiUnderUtbetaling = (productId === oldIpsWithGarantiProductId || productId === ipsWithGarantiProductId) && underUtbetaling;
	return garantiProdukt || ipsGarantiUnderUtbetaling;
};

const isProductType = <T extends ProductType>(productType: T) => {
	return (policy: PolicyView): policy is PolicyView & { productType: T } => {
		return policy.produkttype === productType;
	};
};

export const isITP = isProductType("ITP");
export const isSL = isProductType("SL");
export const isIPS = (id: string) => IPS_PRODUCT_IDS.includes(id);
export const isGammelIPS = (id: string) => OLD_IPS_PRODUCT_IDS.includes(id);
export const isInvesteringsvalgIPS = (id: string) => IPS_INVESTERINGSVALG_PRODUCT_IDS.includes(id);
export const isGarantiIPS = (id: string) => IPS_GARANTI_PRODUCT_IDS.includes(id);
export const isPSK = (id: string) => PENSION_PRODUCT_IDS.includes(id);
export const isEtterlatteProdukt = (id: string) => ETTERLATTE_PRODUCT_IDS.includes(id);
export const isHybrid = (id: string) => HYBRID_PRODUCT_IDS.includes(id);

const sortOrder = [
	"75", // EPK
	"76", // EPK LO
	ipsProductId,
	ipsLoProductId,
	ipsWithGarantiProductId,
	ipsWithGarantiLoProductId,
	pensjonskontoProductId,
	pensjonskontoLoProductId,
	oldPensjonskontoProductId, // Pensjonssparekonto (med fritak)
	loProductId, // LO Favør pensjon
	itpMedInvesteringsvalg, // Investeringsvalg
	oldIpsProductId, // Indv. Pensjonsspareavtale (IPS)
	"78", // PKB etter EPK LO
	pkbEtterEpkProductId, // PKB etter EPK
	"43", // PKB, etter utmelding
	"46", // PKB Investeringsvalg
	"47", // PKB garanti
	pkbLivrenteProductId,
	pkbLivrenteGarantiProductId,
	"50", // PKB livrente gamle skatteregler
	"1", // Fripolise
	"2", // Fripolise etter kollektiv livrente
	"6", // IPA
	ipaProductId,
	ipaEtterlatteProductId, // IPA etterlatte
	"58", // IPS nådd alder
	"31", // Garanti
	klvrLivrenteProductId,
	klvrLivrenteGarantiProductId,
	"43", // Livrente gamle skatteregler
	"7", // Individuell livrente
	fundAccount, // Fondskonto
	livrenteProductId, // Livrente uten tilbakebetaling
	livrenteEtterlatteProductId, // Livrente etterlatte
	garantiProductId, // Garantikonto
	"8",
	"9",
	hybridMedInvesteringsvalg,
	pbgProductId, // Pensjonsbevis med garanti
];

export const ipsSisteFrist = {
	sisteBankdag: 23,
	fjernesAr: 2026,
};

export const isFlexiblePolicy = (productId) => [fundAccount, pensjonskontoProductId, pensjonskontoLoProductId].includes(productId);

export const hasFlexibleProductTaxRules = (productId) => [oldPensjonskontoProductId, fundAccount, pensjonskontoProductId, pensjonskontoLoProductId].includes(productId);

export const showSpareavtale = (productId) =>
	[
		ipsProductId,
		ipsLoProductId,
		ipsWithGarantiProductId,
		ipsWithGarantiLoProductId,
		oldPensjonskontoProductId,
		fundAccount,
		livrenteProductId,
		pensjonskontoProductId,
		pensjonskontoLoProductId,
	].includes(productId);

export const showTaxFreeAmount = (productId) =>
	[
		oldPensjonskontoProductId,
		livrenteProductId,
		klvrLivrenteProductId,
		klvrLivrenteGarantiProductId,
		pkbLivrenteProductId,
		pkbLivrenteGarantiProductId,
		pensjonskontoProductId,
		fundAccount,
	].includes(productId);

export const canEditBeneficiaries = (productId) =>
	[fundAccount, oldPensjonskontoProductId, pensjonskontoProductId, pensjonskontoLoProductId, livrenteProductId, garantiProductId, ipaProductId].includes(productId);

export const canHaveBeneficiaries = (productId) => canEditBeneficiaries(productId) || productId === ipaProductId;

function compareSortOrder(a, b, order) {
	let indexA = order.indexOf(a.produktId);
	if (indexA === -1) {
		indexA = a.polisenummer;
	}
	let indexB = order.indexOf(b.produktId);
	if (indexB === -1) {
		indexB = b.polisenummer;
	}

	if (indexA === indexB) {
		return a.polisenummer - b.polisenummer;
	}
	return indexA - indexB;
}

export const kapitalforvalter = {
	SB1: "SB1",
	ODIN: "ODIN",
	UNKNOWN: "UNKNOWN",
} as const;

const kapitalforvalterMap = {
	ODIN: "ODIN",
	SB1: `SpareBank${nbsp}1`,
};

export function getKapitalforvalterName(forvalter: keyof typeof kapitalforvalterMap) {
	return kapitalforvalterMap[forvalter] || "";
}

export const savingProductFromAge = (age: number): Products => {
	return age > 49 ? products.IPS : products.PENSJONSKONTO;
};

export function createAvtalegiroReturnUrl(gotoUrl: string) {
	// In older versions of IE11 there's a bug that causes window.location.origin to be undefined
	// https://developer.mozilla.org/en-US/docs/Web/API/Window/location
	let host = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
	if (host.endsWith(":")) {
		host = host.substring(0, host.length - 1);
	}
	return `${host}/${gotoUrl}`;
}

export function sortProducts(list: Policy[]) {
	return list.sort((a, b) => compareSortOrder(a, b, sortOrder));
}

export const FRA_JOBB_PRODUKTER = [
	"76",
	"75",
	"78",
	pkbEtterEpkProductId,
	itpMedInvesteringsvalg,
	"31",
	hybridMedInvesteringsvalg,
	"66",
	"35",
	"46",
	"48",
	"50",
	"49",
	"47",
	"63",
	"1",
	"2",
	"5",
	"KL",
	"YTP",
];

export const EGEN_SPARING_PRODUKTER = ["45", "73", "58", "44", "74", "52", "65", "54", "55", "51", "6", "95", "96", "97"];

export function getIcon(policy: PolicyView, options: { className?: string } = {}): ReactNode {
	const { produktId, produkttype } = policy;
	const utbetalingStatus = policy.utbetaling?.utbetalingStatus;

	if ([UNDER_UTBETALING, UTBETALING_AVSLUTTET].includes(utbetalingStatus || "")) {
		return <Icon fileUrl={krSirkelPilIkon} size="xl" className={options.className} />;
	}
	if (produkttype === "NJORD") {
		return <Icon fileUrl={HakeStjerneIkon} size="xl" className={options.className} />;
	}
	if (FRA_JOBB_PRODUKTER.includes(produktId)) {
		return <Icon fileUrl={MoneyBag} size="xl" className={options.className} />;
	}
	if (EGEN_SPARING_PRODUKTER.includes(produktId)) {
		return <Icon fileUrl={Savings} size="xl" className={options.className} />;
	}
	return <Icon fileUrl={MoneyBag} size="xl" className={options.className} />;
}

export function canHaveDeposits(productType) {
	return productType === "PRIVATE_PENSION";
}

export function shouldShowReportDamageButton(policy, customer) {
	return (
		(DISABLED_COVERAGE_IDS.includes(policy.produktId) &&
			!policy.utbetaling.utbetalingStatus &&
			!(policy.produktId === oldPensjonskontoProductId && customer.age >= 62) &&
			!(policy.produktId === loProductId && customer.age >= 67)) ||
		(DEATH_COVERAGE_PRODUCT_IDS.includes(policy.produktId) && customer.ssn !== policy.forsikretFnr)
	);
}

export const period = {
	ONE_MONTH: "ONE_MONTH",
	THREE_MONTHS: "THREE_MONTHS",
	SIX_MONTHS: "SIX_MONTHS",
	ONE_YEAR: "ONE_YEAR",
	TWO_YEARS: "TWO_YEARS",
	THREE_YEARS: "THREE_YEARS",
	FOUR_YEARS: "FOUR_YEARS",
	FIVE_YEARS: "FIVE_YEARS",
	ALL: "ALL",
} as const;
export type Period = keyof typeof period;

export function sharesPercentageForAge(age: number) {
	if (age < 41) return 100;
	if (age > 67) return 20;
	return recommendedPercentages.ALDERSTILPASSET[age]?.actual;
}

export const allowedSavingDaysOfMonth = ["1", "5", "10", "15", "20", "25"];

const detailsRouting = {
	ITP: "itp",
	PKB: "pkb",
	PRIVATE_PENSION: "privat",
	SL: "sl",
	NJORD: "njord",
	KL: "driftspensjon",
	YTP: "ytp",
} as const;

export const getDetailsPageForPolicy = (policy: { produkttype: ProductType }) => detailsRouting[policy.produkttype];

export const getProgramContentDescription = (stockPercentage) => {
	if (stockPercentage === 0) {
		return `(${formatPercentage(100)} renter)`;
	}
	if (stockPercentage > 0) {
		return `(${formatPercentage(stockPercentage)} aksjer)`;
	}
	return "";
};

export const POLICY_DETAILS_ERROR_MESSAGES = {
	AGREEMENT_DOES_NOT_EXIST: {
		title: "Vi kunne ikke finne denne avtalen i baksystemet",
		text: "Hvis du nettopp har opprettet en ny avtale, så kan det dessverre ta noen minutter før " + "avtalen finner veien til baksystemet. Vennligst prøv igjen om en kort stund.",
	},
	MEDLEM_UTMELDT: {
		title: "Vi kunne ikke finne denne avtalen i baksystemet",
		text: "Denne avtalen er ikke lenger tilgjengelig for deg. Kontakt din arbeidsgiver for mer informasjon.",
	},
	FREMREGN_VENTEDE_TRANSAKSJONER: {
		title: "Avtalen kan ikke vises",
		text: "Denne avtalen kan ikke vises fordi det ikke er saldo på avtalen. Kontakt oss dersom du har spørsmål.",
	},
	PRODUCT_ID_MISMATCH: {
		title: "Avtalen kan ikke vises",
		text: "Det pågår endringer på denne avtalen som gjør at vi ikke kan vise den nå, prøv igjen om 1-2 virkedager.",
	},
	SCHEDULED_DOWNTIME: {
		title: "Her vil det være opplysninger om din konto",
		text: "På grunn av planlagt vedlikehold er dessverre baksystemet utilgjengelig " + "hver dag mellom klokken 01:00 og 06:00. Vi beklager ulempen det medfører.",
	},
	GENERIC: {
		title: "Her vil det være opplysninger om din konto",
		text:
			"Vi har dessverre ikke kontakt med baksystemet for øyeblikket og får derfor ikke " +
			"vist deg all informasjon om produktet ditt. Vennligst prøv igjen ved en senere " +
			"anledning. Vi beklager ulempen det medfører.",
	},
} as const;

/*
export const PRODUCT_TAGS = {
	// Vises på produkter som: Flyttes fra oss og på produkter som har kapital som flyttes inn på avtalen
	FLYTTING: {
		defaultText: 'Flytting pågår',
		tag: 'flytting',
	},
	// Vises på produkter som: Er under utbetaling og på produkter som har blitt bestilt utbetaling
	UTBETALING: {
		defaultText: 'Under utbetaling',
		tag: 'utbetaling',
	},
	// Vises på produkter som er ferdig utbetalt
	UTBETALT: {
		defaultText: 'Ferdig utbetalt',
		tag: 'utbetalt',
	},
	// Vises på produkter som: Har prisfordel (LOfavør eller privat sparing med fordel)
	FORDEL: {
		defaultText: 'Prisfordel',
		tag: 'prisfordel',
	},
};
 */

type ProduktTag = {
	variant: "info" | "success" | "warning" | "critical" | "neutral" | "tip";
	tekst: string;
};

export const getProductTags = (policy: PolicyView | F2100PolicyDetailsDto | GablerPoliseDetaljerDto, lowerCostAdvantage: boolean) => {
	const { produktId: productId } = policy;
	const f2100Policy = (policy as F2100PolicyDetailsDto) || (policy as PolicyView);
	const gablerPolise = policy as GablerPoliseDetaljerDto;
	const gablerUtbetalingStatus = gablerPolise.dekninger?.find((d) => d.type === "AP")?.underUtbetaling;
	const utbetalingStatus = f2100Policy.utbetaling?.utbetalingStatus;
	const epkDetails = policy.epkDetaljer;

	if (epkDetails?.flytteprosess?.flyttestatus === "UNDER_FLYTTING" && epkDetails?.flytteprosess.flytteretning === "FLYTTE_TIL") {
		return ["Pensjon på vei"];
	}

	const tags: (string | ProduktTag)[] = [];

	if ((lowerCostAdvantage && PRODUCT_IDS_WITH_LOWER_COST_ADVANTAGE.includes(productId)) || isLoProdukt(productId)) {
		tags.push("Prisfordel");
	}

	if (epkDetails?.flytteprosess?.flyttestatus === "UNDER_FLYTTING" || (epkDetails?.innkommendePkber?.length ?? 0) > 0) {
		tags.push("Flytting pågår");
	}

	if (
		policy.produktId !== innskuddtjenestepensjonMedGarantiProductId &&
		([UNDER_UTBETALING, UTBETALING_BESTILT, AKTIV_UNDER_UTBETALING].includes(utbetalingStatus ?? "") || gablerUtbetalingStatus)
	) {
		tags.push("Under utbetaling");
	}

	if (utbetalingStatus === UTBETALING_AVSLUTTET) {
		tags.push("Ferdig utbetalt");
	}

	if (policy.smartjustering?.aktiv) {
		tags.push({ variant: "success", tekst: "Smartjustert" });
	}

	return tags;
};

export function isLoProdukt(produktId: string): boolean {
	return [loEpkProductId, loPbkProductId].includes(produktId);
}

export type ProduktType = "epk" | "ips" | "pkb" | "livrente" | "ipa" | "psk";
export function hentProduktNavn(produkt?: ProduktType): string {
	switch (produkt) {
		case "epk":
			return "Egen pensjonskonto";
		case "ips":
			return "IPS";
		case "pkb":
			return "Pensjonskapitalbevis";
		case "livrente":
			return "Livrente";
		case "ipa":
			return "Individuell pensjonsavtale (IPA)";
		case "psk":
			return "Pensjonssparekonto";
		default:
			return "Pensjonsavtale";
	}
}
